import React, { useCallback, useEffect } from 'react';
import { useTranslation } from '../../translation';
import { NotificationViewModel } from '../../components/notification/notification';
import { isFunction } from '../../../Shared/utils/is-function';
import { Text } from '../text';

import * as styles from "./language-selector.less";
import { Language, LanguageOptions } from '../../webgiving-generated';
import { GoogleAnalyticsWrapper } from '../../infrastructure/google-analytics-wrapper';

export interface ILanguageSelector {
	tradingName: string; 
	paymentLabel: string; 
	setAppLoading?: (isLoading: boolean) => void;
	unavailableLanguages?:  Record<string, Language>;
	failedToLoadLanguage?: (notification: NotificationViewModel, language: Language) => void;
}

export const LanguageSelector = ({ tradingName, paymentLabel, failedToLoadLanguage, unavailableLanguages, setAppLoading }: ILanguageSelector ) => {
	const { en, es } = LanguageOptions;
	const { language, changeLanguage, onFailedLoading, onLanguageChange, translate } = useTranslation();

	const onChange = useCallback(() => {
		if (isFunction(setAppLoading)) {
			setAppLoading(true);
			onLanguageChange(() => setAppLoading(false));
		};
		const toLanguageCode = language === en.TwoLetterIsoCode ? es.TwoLetterIsoCode: en.TwoLetterIsoCode;
		changeLanguage(toLanguageCode);
		GoogleAnalyticsWrapper.sendEvent('WebGiving Page View', 'ToggleLanguage', `language-${toLanguageCode}`);
	}, [changeLanguage, onLanguageChange, setAppLoading, language]);

	useEffect(() => {
		if (!!tradingName && !!paymentLabel) {
			const title = translate(`${paymentLabel} {{tradingName}}` as any, {tradingName});
			document.title = title;
		}

		return onFailedLoading((lang: string) => {
			const notification = new NotificationViewModel( 
				lang === es.TwoLetterIsoCode
				? 'Lo sentimos, no podemos procesar su solicitud en este momento.'
				: 'Sorry, we are unable to process your request at this time.', 10, 
				false, undefined, true);
			return failedToLoadLanguage && failedToLoadLanguage(notification, LanguageOptions[lang]);
		})
	}, [onFailedLoading, failedToLoadLanguage, translate, language]);

	return (
		<div className={styles.container}>
			<button
				type="button"
				className={styles.button}
				onClick={onChange}
				data-pp-at-target="language-toggle"
				aria-label="Language toggle"
				disabled={!!Object.keys(unavailableLanguages || {}).length}
			>
				<Text>{language === en.TwoLetterIsoCode ? es.Name: en.Name}</Text>
			</button>
		</div>
	);
}
